import React from 'react';

/**
 * V14.5 Part 16 — Top-level ErrorBoundary for the SPA.
 *
 * Without this, any crash during render (failed lazy-chunk load, undefined
 * field access, API client throw, etc.) unmounts the entire React tree and
 * leaves the user staring at a blank white screen. Some learners reported
 * exactly this after entering an OTP — the dashboard route mounted, a
 * downstream component threw, and React's default behaviour ate the error.
 *
 * Boundary behaviour:
 *   • Friendly "Something went wrong" fallback with brand colours.
 *   • Big Reload button — fixes 80% of cases (stale JS chunk, transient API).
 *   • Sign-out fallback — fixes the rest (stale token, missing /me data).
 *   • Posts the error + stack + UA + URL to `/api/client-errors` so we have
 *     a server-side trail when the user reports "blank screen" next time.
 *     Best-effort — boundary never depends on the POST succeeding.
 *
 * Mounted ONCE at the root in `App.js`, wrapping `<AppRoutes />`.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    // Fire-and-forget log to backend. The boundary's UX must NOT depend on
    // this completing — production users on a flaky network shouldn't see
    // a stalled fallback.
    try {
      const backend = process.env.REACT_APP_BACKEND_URL;
      if (backend) {
        const token = localStorage.getItem('ss_token');
        fetch(`${backend}/api/client-errors`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          keepalive: true,  // survives a tab close
          body: JSON.stringify({
            kind: 'react_error_boundary',
            message: String(error?.message || error || 'unknown'),
            stack: String(error?.stack || '').slice(0, 4000),
            component_stack: String(errorInfo?.componentStack || '').slice(0, 4000),
            url: window.location.href,
            user_agent: navigator.userAgent,
            ts: new Date().toISOString(),
          }),
        }).catch(() => { /* swallow */ });
      }
    } catch { /* never let logging crash the fallback */ }
    // Also log to console so devs see it in DevTools.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, errorInfo);
  }

  handleReload = () => {
    // Hard reload — bypasses cache for the navigation request. Most blank-
    // screen cases come from stale JS bundle vs fresh API; a hard reload
    // fixes both.
    try { window.location.reload(); } catch { /* noop */ }
  };

  handleSignOut = () => {
    try {
      localStorage.removeItem('ss_token');
      window.location.href = '/login';
    } catch { /* noop */ }
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const errMsg = String(this.state.error?.message || this.state.error || 'Unknown error');
    return (
      <div
        className="min-h-screen flex items-center justify-center p-4 sm:p-6"
        style={{
          background: 'linear-gradient(135deg, #fff7f5 0%, #ffffff 50%, #fff5f9 100%)',
          fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
        }}
        data-testid="error-boundary-fallback"
      >
        <div
          style={{
            maxWidth: 520,
            width: '100%',
            background: '#ffffff',
            borderRadius: 20,
            border: '2px solid #fee2e2',
            padding: '32px 28px',
            textAlign: 'center',
            boxShadow: '0 8px 24px rgba(225,29,72,0.08)',
          }}
        >
          <div
            style={{
              width: 64, height: 64, borderRadius: 32, background: '#fee2e2',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 20px',
            }}
            aria-hidden="true"
          >
            <span style={{ fontSize: 32 }}>⚠️</span>
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 600, color: '#1f2937', marginBottom: 8 }}>
            Something went wrong
          </h1>
          <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.6, marginBottom: 20 }}>
            We hit a small hiccup loading this page. Refreshing usually fixes it.
            If it keeps happening, request a callback from the app and our team will call you within 24 hours (except Sunday).
          </p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={this.handleReload}
              data-testid="error-boundary-reload"
              style={{
                background: '#be123c', color: '#fff', border: 'none',
                padding: '10px 20px', borderRadius: 10, cursor: 'pointer',
                fontSize: 14, fontWeight: 500,
              }}
            >
              Reload page
            </button>
            <button
              onClick={this.handleSignOut}
              data-testid="error-boundary-signout"
              style={{
                background: '#ffffff', color: '#374151', border: '1px solid #d1d5db',
                padding: '10px 20px', borderRadius: 10, cursor: 'pointer',
                fontSize: 14, fontWeight: 500,
              }}
            >
              Sign out
            </button>
          </div>
          {/* Collapsible diagnostic for power users / support */}
          <details style={{ marginTop: 20, textAlign: 'left' }}>
            <summary style={{ fontSize: 12, color: '#9ca3af', cursor: 'pointer' }}>
              Technical details
            </summary>
            <pre
              style={{
                fontSize: 11, color: '#6b7280', background: '#f9fafb',
                padding: 10, borderRadius: 8, marginTop: 8,
                whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                maxHeight: 200, overflow: 'auto',
              }}
            >
              {errMsg}
            </pre>
          </details>
        </div>
      </div>
    );
  }
}
